import { Component, OnDestroy, OnInit, Output, EventEmitter, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { F1065SchBService } from './f1065-sch-b.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { MatSnackBar } from '@angular/material/snack-bar';

interface FormRow {
  num: string;
  description: string;
  value: any;
  value_user_input?: string;
  value_user_input2?: string;
  conditionalField?: boolean;
  saveParams: string[];
  line_order: string;
  lineAttrKey_a?: string;
  attrib_name_a?: string;
  attrib_order_a?: string;
  attrib_value_a?: string;
  attrib_mandatory_a?: boolean;
  attrib_mandatory_color_a?: boolean;
  lineAttrKey_b?: string;
  attrib_name_b?: string;
  attrib_order_b?: string;
  attrib_value_b?: string;
  lineAttrKey_c?: string;
  attrib_name_c?: string;
  attrib_order_c?: string;
  attrib_value_c?: string;
  attrib_mandatory_c?: boolean;
  attrib_mandatory_color_c?: boolean;
  lineAttrKey_d?: string;
  attrib_name_d?: string;
  attrib_order_d?: string;
  attrib_value_d?: any;
  attrib_mandatory_d?: boolean;
  attrib_mandatory_color_d?: boolean;
  lineAttrKey_e?: string;
  attrib_name_e?: string;
  attrib_order_e?: string;
  attrib_value_e?: string;
  attrib_mandatory_e?: boolean;
  attrib_mandatory_color_e?: boolean;
  lineAttrKey_f?: string;
  attrib_name_f?: string;
  attrib_order_f?: string;
  attrib_value_f?: any[];
  attrib_mandatory_f?: boolean;
  attrib_mandatory_color_f?: boolean;
  JSON_DATA?: any[];
  selected_state_id?: string;
  selected_country_id?: string;
}

interface CountryOption {
  id: string;
  label: string;
}

@Component({
    selector: 'gtw-f1065-sch-b',
    templateUrl: './f1065-sch-b.component.html',
    styleUrls: ['./f1065-sch-b.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065SchBComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  baseUrl: string = '';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  // Parsed inputs
  rowDataObj: any = {};
  filterParamsObj: any = {};
  globalParamsObj: any = {};

  // Component state
  showFormLoader = true;
  isSaving = false;
  minimise = false;

  // Data
  modalTitle = 'Schedule B Other Information';
  title = '';
  taxYear = 0;
  scenarioLabel = '';
  issueDesc = '';

  // Form data
  scheduleBFormView: { rows: FormRow[] } = { rows: [] };
  originalData: { rows: FormRow[] } = { rows: [] };

  // Dropdown options
  allCountries: CountryOption[] = [];
  allUSStates: CountryOption[] = [];
  USCountry_index = 0;

  // Entity info
  entityList: { [key: string]: any } = {};

  // Sub-table data
  entitiesData: any[] = [];
  individualsList: any[] = [];
  corporationList: any[] = [];
  entityList2: any[] = [];

  // Cached PR block indices (set after tax year is known)
  prAddressIdx = -1;
  designatedAddressIdx = -1;

  constructor(
    private schBService: F1065SchBService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);

    this.baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    this.rowDataObj = rowPayloadFromActiveScreen(activeScreen) || {};
    this.filterParamsObj = normalizeFilterParams(activeScreen) || {};
    this.globalParamsObj = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};

    this.taxYear = this.rowDataObj?.TAX_YEAR || 0;
    this.prAddressIdx = this.getPrAddressIndex();
    this.designatedAddressIdx = this.getDesignatedIndividualAddressIndex();
    this.scenarioLabel =
      this.filterParamsObj?.scenarioLabel ||
      this.filterParamsObj?.scenario_label ||
      this.globalParamsObj?.scenarioLabel ||
      this.globalParamsObj?.scenario_desc ||
      this.gtwApp?.USER_SETTINGS?.scenarioLabel ||
      '';
    this.issueDesc = this.rowDataObj?.ISSUE_DESC || '';

    if (this.rowDataObj?.TAX_YEAR) {
      this.title = `${this.modalTitle} ( Tax Year : ${this.rowDataObj.TAX_YEAR} - Scenario : ${this.scenarioLabel} )`;
      this.entityList = {
        'Tax Year': this.rowDataObj.TAX_YEAR,
        'LEID': this.rowDataObj.LEID,
        'LE Name': this.rowDataObj.LE_NAME
      };
    }
  }

  private tryInit(): void {
    if (!this.baseUrl || !this.rowDataObj || Object.keys(this.rowDataObj).length === 0) return;

    const contextKey = [
      this.rowDataObj?.COMBINATION_KEY,
      this.rowDataObj?.TAX_YEAR,
      this.rowDataObj?.SCENARIO_KEY,
      this.baseUrl,
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    this.loadData();
  }

  private loadData(): void {
    this.showFormLoader = true;

    // Load country list first, then state list, then sub-table and form data
    this.schBService.loadCountryList(this.baseUrl, this.globalParamsObj, this.rowDataObj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (countryData) => {
        this.allCountries = [];
        if (countryData.jsonObject) {
          countryData.jsonObject.forEach((country: any, index: number) => {
            if (country.COUNTRY_CODE === 'US') {
              this.USCountry_index = index;
            }
            this.allCountries.push({
              id: country.COUNTRY_CODE,
              label: country.COUNTRY_DESC
            });
          });
        }

        this.schBService.loadUSStateList(this.baseUrl, this.globalParamsObj, this.rowDataObj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (stateData) => {
            this.allUSStates = [];
            if (stateData.jsonObject) {
              stateData.jsonObject.forEach((state: any) => {
                this.allUSStates.push({
                  id: state.STATE_CODE,
                  label: state.STATE_DESC
                });
              });
            }

            // Now load sub-table and form data
            this.loadSubTableAndFormData();
          },
          error: (err) => {
            console.error('Error loading US states:', err);
            this.showFormLoader = false;
          }
        });
      },
      error: (err) => {
        console.error('Error loading countries:', err);
        this.showFormLoader = false;
      }
    });
  }

  private loadSubTableAndFormData(): void {
    // Load sub-table data
    this.schBService.loadSubTableData(this.baseUrl, this.globalParamsObj, this.rowDataObj).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (subTableData) => {
        this.entitiesData = [];
        this.individualsList = [];
        this.corporationList = [];
        this.entityList2 = [];

        if (subTableData.jsonObject) {
          subTableData.jsonObject.forEach((row: any) => {
            if (row.LINE_NO === '2a1') {
              row.DATA_ATTRIB_D = this.findCountryById(row.DATA_ATTRIB_D);
              row.DATA_ATTRIB_E = parseFloat(row.DATA_ATTRIB_E) || 0;
              this.entitiesData.push(row);
            } else if (row.LINE_NO === '2b1') {
              row.DATA_ATTRIB_C = this.findCountryById(row.DATA_ATTRIB_C);
              row.DATA_ATTRIB_D = parseFloat(row.DATA_ATTRIB_D) || 0;
              this.individualsList.push(row);
            } else if (row.LINE_NO === '3a1') {
              row.DATA_ATTRIB_C = this.findCountryById(row.DATA_ATTRIB_C);
              row.DATA_ATTRIB_D = parseFloat(row.DATA_ATTRIB_D) || 0;
              this.corporationList.push(row);
            } else {
              row.DATA_ATTRIB_D = this.findCountryById(row.DATA_ATTRIB_D);
              row.DATA_ATTRIB_E = parseFloat(row.DATA_ATTRIB_E) || 0;
              this.entityList2.push(row);
            }
          });
        }

        // Now load form data
        this.loadFormData();
      },
      error: (err) => {
        console.error('Error loading sub-table data:', err);
        this.loadFormData();
      }
    });
  }

  private findCountryById(countryId: string): CountryOption[] {
    if (!countryId) return [];
    const found = this.allCountries.find(c => c.id === countryId);
    return found ? [found] : [];
  }

  private loadFormData(): void {
    this.schBService.loadFormData(
      this.baseUrl,
      this.globalParamsObj,
      this.rowDataObj
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.processFormData(data);
        this.showFormLoader = false;
      },
      error: (err) => {
        console.error('Error loading form data:', err);
        this.showFormLoader = false;
      }
    });
  }

  private processFormData(data: any): void {
    if (!data.irsForm?.formItemList) return;

    this.scheduleBFormView.rows = [];

    for (let i = 1; i < data.irsForm.formItemList.length; i++) {
      const formItem = data.irsForm.formItemList[i];
      if (!formItem.dataRowList?.[0]) continue;

      const saveParams = formItem.dataRowList[0].rowDimensions.object_id.split('~');
      const dataCellList = formItem.dataRowList[0].dataCellList;
      const rowsCount = dataCellList.length;

      const tempDataObj: any = {
        num: dataCellList[0].name,
        description: dataCellList[1].name,
        line_order: saveParams[8],
        saveParams: saveParams
      };

      if (rowsCount > 2) {
        this.iterateArray(rowsCount, dataCellList, tempDataObj);
      }

      this.scheduleBFormView.rows.push(tempDataObj);
    }

    // Apply initial conditional field logic based on tax year
    this.applyInitialConditions();

    // Store original data for reset
    this.originalData = JSON.parse(JSON.stringify(this.scheduleBFormView));
  }

  private iterateArray(count: number, array: any[], tempDataObj: any): void {
    if (count === 8) {
      // Address fields (a-f)
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.attrib_mandatory_a = true;
      tempDataObj.attrib_value_a = array[2].value;

      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];
      tempDataObj.attrib_value_b = array[3].value;

      tempDataObj.lineAttrKey_c = array[4].key.split('~')[1];
      tempDataObj.attrib_name_c = array[4].key.split('~')[0];
      tempDataObj.attrib_order_c = array[4].key.split('~')[2];
      tempDataObj.attrib_value_c = array[4].value;

      tempDataObj.lineAttrKey_d = array[5].key.split('~')[1];
      tempDataObj.attrib_name_d = array[5].key.split('~')[0];
      tempDataObj.attrib_order_d = array[5].key.split('~')[2];

      tempDataObj.lineAttrKey_e = array[6].key.split('~')[1];
      tempDataObj.attrib_name_e = array[6].key.split('~')[0];
      tempDataObj.attrib_order_e = array[6].key.split('~')[2];
      tempDataObj.attrib_value_e = array[6].value;

      tempDataObj.lineAttrKey_f = array[7].key.split('~')[1];
      tempDataObj.attrib_name_f = array[7].key.split('~')[0];
      tempDataObj.attrib_order_f = array[7].key.split('~')[2];
      tempDataObj.attrib_mandatory_f = true;

      if (array[7].value) {
        tempDataObj.attrib_value_f = this.findCountryById(array[7].value);
      } else {
        tempDataObj.attrib_value_f = [this.allCountries[this.USCountry_index]];
      }
      tempDataObj.selected_country_id = tempDataObj.attrib_value_f?.[0]?.id ?? '';

      if (tempDataObj.selected_country_id === 'US') {
        tempDataObj.attrib_mandatory_e = true;
        tempDataObj.attrib_mandatory_d = true;
        tempDataObj.attrib_mandatory_c = true;
        console.log('[SchB DEBUG iterateArray] array[5].value =', JSON.stringify(array[5].value), 'row description =', tempDataObj.description);
        if (array[5].value) {
          const stateFound = this.allUSStates.find(s => s.id === array[5].value);
          tempDataObj.attrib_value_d = stateFound ? [stateFound] : [];
        } else {
          tempDataObj.attrib_value_d = [];
        }
        tempDataObj.selected_state_id = tempDataObj.attrib_value_d[0]?.id ?? '';
        console.log('[SchB DEBUG iterateArray] selected_state_id =', JSON.stringify(tempDataObj.selected_state_id));
      } else {
        tempDataObj.attrib_mandatory_e = false;
        tempDataObj.attrib_mandatory_d = false;
        tempDataObj.attrib_mandatory_c = false;
        tempDataObj.attrib_value_d = array[5].value;
        tempDataObj.selected_state_id = '';
      }

    } else if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];

      const checkboxLines = ['1a', '1b', '1c', '1d', '1e', '11', '32'];
      if (array[2].value === null || array[2].value === '0' || array[2].value === 'NA') {
        tempDataObj.value = checkboxLines.includes(tempDataObj.num) ? false : '';
      } else if (array[2].value === 'true') {
        tempDataObj.value = checkboxLines.includes(tempDataObj.num) ? true : '';
      } else if (array[2].value === 'false') {
        tempDataObj.value = checkboxLines.includes(tempDataObj.num) ? false : '';
      } else {
        tempDataObj.value = array[2].value;
      }

    } else if (count === 4) {
      if (tempDataObj.num === '1f') {
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];
        tempDataObj.value = array[2].value;
        tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
        tempDataObj.attrib_name_b = array[3].key.split('~')[0];
        tempDataObj.attrib_order_b = array[3].key.split('~')[2];
        tempDataObj.value_user_input = array[3].value;

        if (tempDataObj.value === null || tempDataObj.value === 'false' || tempDataObj.value === '0') {
          tempDataObj.value = false;
          tempDataObj.conditionalField = true;
        } else if (tempDataObj.value === 'true') {
          tempDataObj.value = true;
          tempDataObj.conditionalField = false;
        } else {
          tempDataObj.value = false;
          tempDataObj.conditionalField = true;
        }
      } else {
        // Yes/No with sub-table
        tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
        tempDataObj.attrib_name_a = array[2].key.split('~')[0];
        tempDataObj.attrib_order_a = array[2].key.split('~')[2];

        tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
        tempDataObj.attrib_name_b = array[3].key.split('~')[0];
        tempDataObj.attrib_order_b = array[3].key.split('~')[2];

        if (array[2].value === 'true') {
          tempDataObj.value = true;
        } else if (array[3].value === 'true') {
          tempDataObj.value = false;
        } else {
          tempDataObj.value = 'NA';
        }

        // Assign JSON_DATA based on line number
        if (tempDataObj.num === '2a') {
          tempDataObj.JSON_DATA = this.entitiesData;
        } else if (tempDataObj.num === '2b') {
          tempDataObj.JSON_DATA = this.individualsList;
        } else if (tempDataObj.num === '3a') {
          tempDataObj.JSON_DATA = this.corporationList;
        } else {
          tempDataObj.JSON_DATA = this.entityList2;
        }
      }

    } else {
      // Yes/No with conditional input
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];

      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];

      if (array[2].value === 'true') {
        tempDataObj.value = true;
      } else if (array[3].value === 'true') {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = 'NA';
      }

      tempDataObj.lineAttrKey_c = array[4].key.split('~')[1];
      tempDataObj.attrib_name_c = array[4].key.split('~')[0];
      tempDataObj.attrib_order_c = array[4].key.split('~')[2];

      // Handle conditional fields based on tax year
      const conditionalLines = this.getConditionalLines();
      if (conditionalLines.includes(tempDataObj.num)) {
        tempDataObj.value_user_input = array[4].value;
        tempDataObj.conditionalField = tempDataObj.value !== true;
      }

      // Handle special lines with two inputs (e.g., line 29 for <=2020, line 28 for >2020)
      if (this.hasSecondInput(tempDataObj.num)) {
        tempDataObj.lineAttrKey_d = array[array.length - 1].key.split('~')[1];
        tempDataObj.attrib_name_d = array[array.length - 1].key.split('~')[0];
        tempDataObj.attrib_order_d = array[array.length - 1].key.split('~')[2];
        tempDataObj.value_user_input2 = array[array.length - 1].value;
      }
    }
  }

  private getConditionalLines(): string[] {
    if (this.taxYear <= 2020) {
      return ['8', '14', '22', '25', '26', '29'];
    } else if (this.taxYear > 2020 && this.taxYear < 2023) {
      return ['8', '14', '22', '25', '27', '28'];
    } else if (this.taxYear >= 2023) {
      return ['8', '14', '22', '25', '27', '28', '10a', '10b', '10c', '10d'];
    }
    return [];
  }

  private hasSecondInput(lineNum: string): boolean {
    if (this.taxYear <= 2020) {
      return lineNum === '29';
    } else {
      return ['28', '10b', '10c'].includes(lineNum);
    }
  }

  private applyInitialConditions(): void {
    let triggerIndex: number;
    let conditionalIndices: number[];

    if (this.taxYear <= 2020) {
      triggerIndex = 48;
      conditionalIndices = [49, 50, 51, 52, 53, 54];
    } else if (this.taxYear > 2020 && this.taxYear < 2023) {
      triggerIndex = 52;
      conditionalIndices = [53, 54, 55, 56, 57, 58];
    } else if (this.taxYear === 2023) {
      triggerIndex = 57;
      conditionalIndices = [58, 59, 60, 61, 62, 63];
    } else if (this.taxYear >= 2024) {
      triggerIndex = 58;
      conditionalIndices = [60, 61, 62, 63, 64, 65];
    } else {
      return;
    }

    if (this.scheduleBFormView.rows[triggerIndex]?.value === false) {
      this.toggleConditionalField(conditionalIndices, false, true);
    } else {
      this.toggleConditionalField(conditionalIndices, true, true);
    }
  }

  private toggleConditionalField(indices: number[], flag: boolean, isInitial = false): void {
    indices.forEach(index => {
      const row = this.scheduleBFormView.rows[index];
      if (row) {
        if ('conditionalField' in row) {
          row.conditionalField = flag;
        }
        if (flag && !isInitial) {
          row.value = '';
        }

        const addressIndices = this.getAddressIndices();
        if (addressIndices.includes(index) && flag && !isInitial) {
          row.attrib_value_f = [this.allCountries[this.USCountry_index]];
          row.selected_country_id = this.allCountries[this.USCountry_index]?.id ?? '';
          row.attrib_value_e = '';
          row.attrib_value_d = [];
          row.selected_state_id = '';
          row.attrib_value_c = '';
          row.attrib_value_b = '';
          row.attrib_value_a = '';
        }
      }
    });
  }

  private getAddressIndices(): number[] {
    if (this.taxYear <= 2020) {
      return [50, 53];
    } else if (this.taxYear > 2020 && this.taxYear < 2023) {
      return [54, 57];
    } else if (this.taxYear === 2023) {
      return [59, 62];
    } else if (this.taxYear >= 2024) {
      return [61, 64];
    }
    return [];
  }

  // Public methods for template
  
  // Yes/No checkbox helpers - handle both boolean and string values
  isYesChecked(value: any): boolean {
    return value === true || value === 'true';
  }

  isNoChecked(value: any): boolean {
    return value === false || value === 'false';
  }

  onYesClick(index: number): void {
    if (this.scheduleBFormView.rows[index]) {
      this.scheduleBFormView.rows[index].value = true;
      this.fieldVisibility(index, true, 'yes');
    }
  }

  onNoClick(index: number): void {
    if (this.scheduleBFormView.rows[index]) {
      this.scheduleBFormView.rows[index].value = false;
      this.fieldVisibility(index, false, 'no');
    }
  }

  // Check if line is 1a-1f (mutual exclusive checkboxes)
  is1aTo1fLine(lineNum: string): boolean {
    return ['1a', '1b', '1c', '1d', '1e', '1f'].includes(lineNum);
  }

  // Handle 1a-1f checkbox click - only one can be selected at a time
  onCheckbox1aTo1fClick(clickedIndex: number): void {
    const row = this.scheduleBFormView.rows[clickedIndex];
    if (!row) return;

    // Get indices for lines 1a-1f (indices 1-6 based on AngularJS code)
    const indices1aTo1f = [1, 2, 3, 4, 5, 6];
    
    // Check if we're unchecking the currently checked item
    const wasChecked = row.value === true || row.value === 'true';
    
    if (wasChecked) {
      // Just uncheck this one
      row.value = false;
      // Disable 1f input if unchecking 1f
      if (clickedIndex === 6) {
        row.conditionalField = true;
        row.value_user_input = '';
      }
    } else {
      // Uncheck all others first
      indices1aTo1f.forEach(idx => {
        const otherRow = this.scheduleBFormView.rows[idx];
        if (otherRow && idx !== clickedIndex) {
          otherRow.value = false;
        }
      });
      
      // Check this one
      row.value = true;
      
      // Handle 1f conditional input
      if (clickedIndex === 6) {
        // Enable 1f input when 1f is checked
        row.conditionalField = false;
      } else {
        // Disable 1f input when any other checkbox is checked
        const row1f = this.scheduleBFormView.rows[6];
        if (row1f) {
          row1f.conditionalField = true;
          row1f.value_user_input = '';
        }
      }
    }
  }

  fieldVisibility(index: number, state: boolean, name?: string): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;

    // Handle checkbox group 1a-1f (indices 1-6)
    if (index >= 1 && index <= 6) {
      this.handleCheckboxGroup(index, state);
      return;
    }

    // Handle Yes/No checkboxes with conditional fields
    if (name === 'yes' || name === 'no') {
      this.handleYesNoCheckbox(index, state, name);
      return;
    }

    // Handle other checkboxes (11, 32)
    if (row.num === '11' || row.num === '32') {
      // Just toggle value
      return;
    }
  }

  private handleCheckboxGroup(index: number, state: boolean): void {
    // Indices 1-6 represent checkboxes 1a-1f
    // When one is checked, all others should be unchecked
    const allIndices = [1, 2, 3, 4, 5, 6];
    const otherIndices = allIndices.filter(i => i !== index);
    
    // Uncheck all other checkboxes when one is checked
    if (state) {
      otherIndices.forEach(i => {
        if (this.scheduleBFormView.rows[i]) {
          this.scheduleBFormView.rows[i].value = false;
        }
      });
    }
    
    // Row 6 (1f) has a conditional input field
    // It should be enabled only when 1f is checked
    if (this.scheduleBFormView.rows[6]) {
      if (index === 6) {
        // If clicking on 1f itself
        this.scheduleBFormView.rows[6].conditionalField = !state;
      } else {
        // If clicking on any other checkbox, disable 1f input
        this.scheduleBFormView.rows[6].conditionalField = true;
        this.scheduleBFormView.rows[6].value_user_input = '';
      }
    }
  }

  private handleYesNoCheckbox(index: number, state: boolean, name: string): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;

    // Toggle conditional field if exists
    if (row.conditionalField !== undefined) {
      if (name === 'yes') {
        row.conditionalField = false; // Enable input on Yes
      } else if (name === 'no') {
        row.conditionalField = true; // Disable input on No
        row.value_user_input = '';
        if (row.value_user_input2 !== undefined) {
          row.value_user_input2 = '';
        }
      }
    }

    // Special handling for PR block trigger lines
    const prTriggerIndex = this.getPrTriggerIndex();
    if (index === prTriggerIndex) {
      const conditionalIndices = this.getPrConditionalIndices();
      if (name === 'yes' || (name === 'no' && !state)) {
        // Show PR fields
        this.toggleConditionalField(conditionalIndices, false);
      } else {
        // Hide PR fields
        this.toggleConditionalField(conditionalIndices, true);
      }
    }
  }

  private getPrTriggerIndex(): number {
    if (this.taxYear <= 2020) return 48;
    if (this.taxYear > 2020 && this.taxYear < 2023) return 52;
    if (this.taxYear === 2023) return 57;
    if (this.taxYear >= 2024) return 58;
    return -1;
  }

  private getPrConditionalIndices(): number[] {
    if (this.taxYear <= 2020) return [49, 50, 51, 52, 53, 54];
    if (this.taxYear > 2020 && this.taxYear < 2023) return [53, 54, 55, 56, 57, 58];
    if (this.taxYear === 2023) return [58, 59, 60, 61, 62, 63];
    if (this.taxYear >= 2024) return [60, 61, 62, 63, 64, 65];
    return [];
  }

  mandatoryFields(index: number): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;

    if (row.attrib_value_f?.length && row.attrib_value_f[0].id === 'US') {
      row.attrib_mandatory_e = true;
      row.attrib_mandatory_d = true;
      row.attrib_mandatory_c = true;
    } else {
      row.attrib_mandatory_e = false;
      row.attrib_mandatory_d = false;
      row.attrib_mandatory_c = false;
    }
  }

  onCountryChange(index: number): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;

    if (row.attrib_value_f?.[0]?.id === 'US') {
      row.attrib_value_d = [];
      row.selected_state_id = '';
    } else {
      row.attrib_value_d = '';
      row.selected_state_id = '';
    }
    this.mandatoryFields(index);
  }

  onSelectedCountryChange(index: number): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;
    const found = this.allCountries.find(c => c.id === row.selected_country_id);
    row.attrib_value_f = found ? [found] : [];
    this.onCountryChange(index);
  }

  onSelectedStateChange(index: number): void {
    const row = this.scheduleBFormView.rows[index];
    if (!row) return;
    const found = this.allUSStates.find(s => s.id === row.selected_state_id);
    row.attrib_value_d = found ? [found] : [];
  }

  addRow(type: string, parentIndex: number): void {
    const row = this.scheduleBFormView.rows[parentIndex];
    if (!row?.JSON_DATA) return;

    const newRow: any = {
      OCCURRENCE: row.JSON_DATA.length + 1,
      DATA_ATTRIB_A: '',
      DATA_ATTRIB_B: '',
      DATA_ATTRIB_C: [this.allCountries[this.USCountry_index]],
      DATA_ATTRIB_D: ''
    };

    if (type === 'entity') {
      newRow.DATA_ATTRIB_C = '';
      newRow.DATA_ATTRIB_D = [this.allCountries[this.USCountry_index]];
      newRow.DATA_ATTRIB_E = '';
    }

    row.JSON_DATA.push(newRow);
  }

  removeRow(parentIndex: number, rowIndex: number): void {
    const row = this.scheduleBFormView.rows[parentIndex];
    if (row?.JSON_DATA) {
      row.JSON_DATA.splice(rowIndex, 1);
    }
  }

  limitDecimal(value: any, line: string, index: number, attr: string, ctrl?: any): void {
    const rowIndex = this.scheduleBFormView.rows.findIndex(r => r.num === line);
    if (rowIndex >= 0 && this.scheduleBFormView.rows[rowIndex]?.JSON_DATA?.[index]) {
      if (Number(value) > 100) {
        this.scheduleBFormView.rows[rowIndex].JSON_DATA![index][attr] = 100;
        ctrl?.control?.setValue(100);
      }
    }
  }

  onlyPercentage(value: any, line: string, index: number, attr: string): void {
    const rowIndex = this.scheduleBFormView.rows.findIndex(r => r.num === line);
    if (rowIndex >= 0 && Number(value) > 100) {
      (this.scheduleBFormView.rows[rowIndex] as any)[attr] = 100;
    }
  }

  isValid(): boolean {
    let hasNoError = true;
    const addressIndices = this.getAddressIndices();
    const triggerIndex = this.getTriggerIndex();

    if (triggerIndex >= 0 && !this.scheduleBFormView.rows[triggerIndex]?.value) {
      addressIndices.forEach(index => {
        const row = this.scheduleBFormView.rows[index];
        if (!row) return;

        if (!row.attrib_value_a) {
          hasNoError = false;
          row.attrib_mandatory_color_a = true;
        }
        if (!row.attrib_value_f?.length) {
          hasNoError = false;
          row.attrib_mandatory_color_f = true;
        }
        if (row.attrib_value_f?.length && row.attrib_value_f[0].id === 'US') {
          if (!row.attrib_value_c) {
            hasNoError = false;
            row.attrib_mandatory_color_c = true;
          }
          if (!row.attrib_value_d?.length) {
            hasNoError = false;
            row.attrib_mandatory_color_d = true;
          }
          if (!row.attrib_value_e) {
            hasNoError = false;
            row.attrib_mandatory_color_e = true;
          }
        }
      });
    }

    return hasNoError;
  }

  private getTriggerIndex(): number {
    if (this.taxYear <= 2020) return 48;
    if (this.taxYear > 2020 && this.taxYear < 2023) return 52;
    if (this.taxYear === 2023) return 57;
    if (this.taxYear >= 2024) return 59;
    return -1;
  }

  onSave(): void {
    if (!this.isValid()) {
      this.emitErrorEvent('Please provide the mandatory fields.');
      return;
    }

    this.isSaving = true;
    const formData = this.buildSavePayload();

    this.schBService.saveScheduleBForm(
      this.baseUrl,
      formData,
      this.rowDataObj,
      this.filterParamsObj,
      this.globalParamsObj
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.callSuccess === '1' && (response.errorMessage === 'null' || response.errorMessage === null)) {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.emitGridUpdateEvent();
        } else {
          const errorMsg = response.errorMessage || 'Error occurred in saving the Transaction. If this continues please contact support';
          console.error('Save error:', errorMsg);
          this.emitErrorEvent(errorMsg);
        }
      },
      error: (err) => {
        this.isSaving = false;
        const errorMsg = err?.message || 'Error occurred in saving the Transaction. If this continues please contact support';
        console.error('Save error:', err);
        this.emitErrorEvent(errorMsg);
      }
    });
  }

  private emitErrorEvent(message: string): void {
    console.error('[F1065SchB]', message);
  }

  private buildSavePayload(): any[] {
    const tempArray: any[] = [];
    const formData = {
      addrows: [],
      editrows: [tempArray],
      removerows: []
    };

    this.scheduleBFormView.rows.forEach((row, i) => {
      const formRowDataA: any = {
        tax_year: this.rowDataObj.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowDataObj.COMBINATION_KEY,
        group_obj_key: this.rowDataObj.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value === true ? 'true' : row.value === 'NA' ? 'false' : row.value === false ? 'false' : row.value?.toString() || '',
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: '',
        JSON_DATA: []
      };

      // Handle sub-table JSON_DATA
      const subTableLines = ['3a1', '3b1', '2a1', '2b1'];
      if (subTableLines.includes(formRowDataA.line_no)) {
        const prevRow = this.scheduleBFormView.rows[i - 1];
        if (prevRow?.value === true && prevRow?.JSON_DATA?.length) {
          formRowDataA.JSON_DATA = this.buildJsonDataArray(formRowDataA.line_no, prevRow.JSON_DATA);
        }
      }

      tempArray.push(formRowDataA);

      // Add additional attributes (b, c, d, e, f)
      this.addAdditionalAttributes(row, i, tempArray);
    });

    return [formData];
  }

  private buildJsonDataArray(lineNo: string, jsonData: any[]): any[] {
    const attributes = ['2b1', '3a1'].includes(lineNo) ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E'];
    const result: any[] = [];

    jsonData.forEach((rowData, rowIndex) => {
      let hasData = false;
      const tempDataArray: any[] = [];

      attributes.forEach(attr => {
        const attrValue = rowData[`DATA_ATTRIB_${attr}`];
        let tempValue = '';
        
        if (Array.isArray(attrValue)) {
          tempValue = attrValue.length ? attrValue[0].id : '';
        } else {
          tempValue = attrValue || '';
        }

        const tempObj = {
          OCCURRENCE: rowIndex + 1,
          ATTRIB_NAME: `DATA_ATTRIB_${attr}`,
          ATTRIB_VALUE: tempValue
        };

        if (tempObj.ATTRIB_VALUE !== undefined && tempObj.ATTRIB_VALUE !== '') {
          hasData = true;
        }
        tempDataArray.push(tempObj);
      });

      if (hasData) {
        result.push(...tempDataArray);
      }
    });

    return result;
  }

  private addAdditionalAttributes(row: FormRow, index: number, tempArray: any[]): void {
    // Add attribute B
    if (row.attrib_name_b) {
      const formRowDataB: any = {
        tax_year: this.rowDataObj.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_b,
        line_description: row.description,
        combination_key: this.rowDataObj.COMBINATION_KEY,
        group_obj_key: this.rowDataObj.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_b,
        Attribute_Name: row.attrib_name_b,
        Attribute_Value: row.num === '1f' ? row.value_user_input : (row.value === true ? 'false' : row.value === 'NA' ? 'false' : row.value === false ? 'true' : row.value?.toString() || ''),
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataB);
    }

    // Add attribute C
    if (row.attrib_name_c) {
      const formRowDataC: any = {
        tax_year: this.rowDataObj.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_c,
        line_description: row.description,
        combination_key: this.rowDataObj.COMBINATION_KEY,
        group_obj_key: this.rowDataObj.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_c,
        Attribute_Name: row.attrib_name_c,
        Attribute_Value: row.value_user_input,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataC);
    }

    // Add attribute D (for specific lines)
    if (row.attrib_name_d && this.hasSecondInput(row.num)) {
      const formRowDataD: any = {
        tax_year: this.rowDataObj.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_d,
        line_description: row.description,
        combination_key: this.rowDataObj.COMBINATION_KEY,
        group_obj_key: this.rowDataObj.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_d,
        Attribute_Name: row.attrib_name_d,
        Attribute_Value: row.value_user_input2,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataD);
    }

    // Add address fields for specific indices
    const addressIndices = this.getAddressIndices();
    if (addressIndices.includes(index)) {
      ['a', 'b', 'c', 'd', 'e', 'f'].forEach(col => {
        const attrName = (row as any)[`attrib_name_${col}`];
        if (attrName) {
          let tempValue = (row as any)[`attrib_value_${col}`];
          if (Array.isArray(tempValue)) {
            tempValue = tempValue.length ? tempValue[0].id : '';
          }

          tempArray.push({
            tax_year: this.rowDataObj.TAX_YEAR,
            trans_type_key: row.saveParams[1],
            line_order: row.line_order,
            attrib_order: (row as any)[`attrib_order_${col}`],
            line_description: row.description,
            combination_key: this.rowDataObj.COMBINATION_KEY,
            group_obj_key: this.rowDataObj.GROUP_OBJ_KEY,
            form_key: row.saveParams[2],
            line_no: row.num,
            occurence: row.saveParams[7],
            line_attrib_key: (row as any)[`lineAttrKey_${col}`],
            Attribute_Name: attrName,
            Attribute_Value: tempValue || '',
            trans_dtls_key: row.saveParams[3],
            trans_status: row.saveParams[6],
            is_dirty: ''
          });
        }
      });
    }
  }

  private emitGridUpdateEvent(): void {
    this.requestGridUpdate.emit('refresh');
  }

  onReset(): void {
    this.scheduleBFormView = JSON.parse(JSON.stringify(this.originalData));
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper for template - should row be hidden
  shouldHideRow(row: FormRow): boolean {
    const hiddenLines = ['2a1', '2b1', '3a1', '3b1'];
    
    if (this.taxYear <= 2020) {
      hiddenLines.push('25(i)', '25(ii)', '25(iii)', '25(iv)', '25(v)', '25(vi)');
    } else {
      hiddenLines.push('29(i)', '29(ii)', '29(iii)', '29(iv)', '29(v)', '29(vi)');
    }
    
    return hiddenLines.includes(row.num);
  }

  // Helper for template - is checkbox type
  isCheckboxLine(lineNum: string): boolean {
    return ['1a', '1b', '1c', '1d', '1e', '1f', '11', '32'].includes(lineNum);
  }

  // Helper for template - is yes/no type
  isYesNoLine(lineNum: string): boolean {
    const yesNoLines = ['2a', '2b', '3a', '3b', '4d', '5', '6', '7', '8', '9', '10a', '10b', '10c', '12', '14', '16a', '16b', '19', '20', '21', '22', '23', '24', '25', '26', '28', '29'];
    return yesNoLines.includes(lineNum);
  }

  // Helper for template - has sub-table
  hasSubTable(lineNum: string, parentIndex: number): boolean {
    const row = this.scheduleBFormView.rows[parentIndex];
    if (!row) return false;

    if (lineNum === '2a' && this.scheduleBFormView.rows[8]?.value === true) return true;
    if (lineNum === '2b' && this.scheduleBFormView.rows[10]?.value === true) return true;
    if (lineNum === '3a' && this.scheduleBFormView.rows[13]?.value === true) return true;
    if (lineNum === '3b' && this.scheduleBFormView.rows[15]?.value === true) return true;
    
    return false;
  }

  // Helper methods for PR Block indices based on tax year
  getPrBlockLine(): string {
    if (this.taxYear <= 2020) return '25';
    if (this.taxYear === 2023) return '31';
    if (this.taxYear >= 2024) return '33';
    return '29';
  }

  getPrNameIndex(): number {
    if (this.taxYear <= 2020) return 49;
    if (this.taxYear === 2023) return 58;
    if (this.taxYear >= 2024) return 60;
    return 53;
  }

  getPrAddressIndex(): number {
    if (this.taxYear <= 2020) return 50;
    if (this.taxYear === 2023) return 59;
    if (this.taxYear >= 2024) return 61;
    return 54;
  }

  getPrPhoneIndex(): number {
    if (this.taxYear <= 2020) return 51;
    if (this.taxYear === 2023) return 60;
    if (this.taxYear >= 2024) return 62;
    return 55;
  }

  getDesignatedIndividualNameIndex(): number {
    if (this.taxYear <= 2020) return 52;
    if (this.taxYear === 2023) return 61;
    if (this.taxYear >= 2024) return 63;
    return 56;
  }

  getDesignatedIndividualAddressIndex(): number {
    if (this.taxYear <= 2020) return 53;
    if (this.taxYear === 2023) return 62;
    if (this.taxYear >= 2024) return 64;
    return 57;
  }

  getDesignatedIndividualPhoneIndex(): number {
    if (this.taxYear <= 2020) return 54;
    if (this.taxYear === 2023) return 63;
    if (this.taxYear >= 2024) return 65;
    return 58;
  }

  // Designated individual name is stored as a single full name in `.value`.
  // The UI splits it into two fields on the first space; save still uses the combined value.
  getDesignatedIndividualFirstName(): string {
    const val = this.scheduleBFormView.rows[this.getDesignatedIndividualNameIndex()]?.value;
    if (typeof val !== 'string') {
      return '';
    }
    const spaceIdx = val.indexOf(' ');
    return spaceIdx === -1 ? val : val.substring(0, spaceIdx);
  }

  getDesignatedIndividualLastName(): string {
    const val = this.scheduleBFormView.rows[this.getDesignatedIndividualNameIndex()]?.value;
    if (typeof val !== 'string') {
      return '';
    }
    const spaceIdx = val.indexOf(' ');
    return spaceIdx === -1 ? '' : val.substring(spaceIdx + 1);
  }

  setDesignatedIndividualFirstName(first: string): void {
    this.updateDesignatedIndividualName(first ?? '', this.getDesignatedIndividualLastName());
  }

  setDesignatedIndividualLastName(last: string): void {
    this.updateDesignatedIndividualName(this.getDesignatedIndividualFirstName(), last ?? '');
  }

  private updateDesignatedIndividualName(first: string, last: string): void {
    const row = this.scheduleBFormView.rows[this.getDesignatedIndividualNameIndex()];
    if (!row) {
      return;
    }
    row.value = last ? `${first} ${last}` : first;
  }

  // Vote line based on tax year
  getVoteLine(): string {
    if (this.taxYear <= 2020) return '29';
    return '28';
  }

  // Conditional input lines
  getConditionalInputLines(): string[] {
    if (this.taxYear <= 2020) {
      return ['1f', '8', '14', '22', '25', '26'];
    } else if (this.taxYear === 2023) {
      return ['1f', '8', '10a', '14', '22', '25', '31'];
    } else if (this.taxYear >= 2024) {
      return ['1f', '8', '10a', '14', '22', '25', '33'];
    }
    return ['1f', '8', '14', '22', '25', '29'];
  }

  // Number input lines
  getNumberInputLines(): string[] {
    if (this.taxYear <= 2020) {
      return ['13', '15', '17', '18', '27'];
    }
    return ['13', '15', '17', '18', '26'];
  }

  // Special description lines for 2023 and later
  is10bLine(lineNum: string): boolean {
    return this.taxYear >= 2023 && lineNum === '10b';
  }

  is10cLine(lineNum: string): boolean {
    return this.taxYear >= 2023 && lineNum === '10c';
  }

  is10dLine(lineNum: string): boolean {
    return this.taxYear >= 2023 && lineNum === '10d';
  }

  // Get Yes/No lines based on tax year
  getYesNoLines(): string[] {
    const baseLines = ['2a', '2b', '3a', '3b', '4d', '5', '6', '7', '8', '9', '10a', '10b', '10c', '12', '14', '16a', '16b', '19', '20', '21', '22', '23', '24', '25'];
    if (this.taxYear <= 2020) {
      return [...baseLines, '26', '28', '29'];
    }
    if (this.taxYear === 2023) {
      return [...baseLines, '10d', '27', '28', '29a', '29b', '30', '31'];
    }
    if (this.taxYear >= 2024) {
      return [...baseLines, '10d', '27', '28', '29a', '29b', '30', '33'];
    }
    return [...baseLines, '27', '28', '29'];
  }

  // Override isYesNoLine to use dynamic list
  isYesNoLineForYear(lineNum: string): boolean {
    return this.getYesNoLines().includes(lineNum);
  }

  // Hide row for 2022 special case (line 29 renumbered to 30)
  getDisplayLineNumber(row: FormRow): string {
    if (this.taxYear === 2022 && row.num === '29') {
      return '30';
    }
    return row.num;
  }

  // Show reserved row for 2022
  showReservedLine29(): boolean {
    return this.taxYear === 2022;
  }

  // Check if line should be hidden for specific year
  shouldHideRowForYear(row: FormRow): boolean {
    const hiddenLines = ['2a1', '2b1', '3a1', '3b1'];
    
    if (this.taxYear <= 2020) {
      hiddenLines.push('25(i)', '25(ii)', '25(iii)', '25(iv)', '25(v)', '25(vi)');
    } else if (this.taxYear === 2023) {
      hiddenLines.push('29(i)', '29(ii)', '29(iii)', '29(iv)', '29(v)', '29(vi)');
      hiddenLines.push('31(i)', '31(ii)', '31(iii)', '31(iv)', '31(v)', '31(vi)');
    } else if (this.taxYear >= 2024) {
      hiddenLines.push('29(i)', '29(ii)', '29(iii)', '29(iv)', '29(v)', '29(vi)');
      hiddenLines.push('33(i)', '33(ii)', '33(iii)', '33(iv)', '33(v)', '33(vi)');
    } else {
      hiddenLines.push('29(i)', '29(ii)', '29(iii)', '29(iv)', '29(v)', '29(vi)');
    }
    
    return hiddenLines.includes(row.num);
  }

  // Check if country is US for a given row index
  isCountryUS(index: number): boolean {
    const row = this.scheduleBFormView.rows[index];
    return row?.selected_country_id === 'US' || row?.attrib_value_f?.[0]?.id === 'US';
  }

  // Safe getter for row value
  getRowValue(index: number): any {
    return this.scheduleBFormView.rows[index]?.value;
  }

  // Safe getter for row attribute
  getRowAttr(index: number, attr: string): any {
    const row = this.scheduleBFormView.rows[index];
    return row ? (row as any)[attr] : null;
  }

  // Safe setter for row value
  setRowValue(index: number, value: any): void {
    if (this.scheduleBFormView.rows[index]) {
      this.scheduleBFormView.rows[index].value = value;
    }
  }

  // Safe setter for row attribute
  setRowAttr(index: number, attr: string, value: any): void {
    const row = this.scheduleBFormView.rows[index];
    if (row) {
      (row as any)[attr] = value;
    }
  }

  // Check if row is disabled
  isRowDisabled(index: number): boolean {
    return this.scheduleBFormView.rows[index]?.conditionalField ?? false;
  }

}


